import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, ChevronsUpDown, Download, Info, RotateCw } from "lucide-react";
import { Fragment, useMemo, useState } from "react";
import { toast } from "sonner";

import { Loading } from "@/components/ui/loading";
import { SkeletonLines } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Page, PageHeader, Toolbar } from "@/components/ui/page";
import { RefreshButton } from "@/components/ui/refresh-button";
import { SearchInput } from "@/components/ui/search-input";
import { Segmented } from "@/components/ui/tabs";
import { api } from "@/services/api";
import type { Translate } from "@/constants/i18n";
import { cn } from "@/utils/utils";
import { useTranslation } from "@/store/locale";

/** One tier's counts, with the legacy API's key spellings. */
interface Counts {
  business: number;
  gold: number;
  Affiliates: number;
  employee: number;
  complimentary: number;
  total: number;
}

interface ClubRow {
  club_id: string;
  club_name: string;
  primary: Counts;
  Supplementary: Counts;
}

interface Analytics {
  member_data: ClubRow[];
  /** When the worker last counted; null before its first run. */
  refreshed_at: string | null;
  /** Whether any member was written since that count. */
  changed_since_refresh: boolean;
}

/** How old a count has to be before the screen offers a recount; the API refuses sooner. */
const RECOUNT_AFTER_MS = 10 * 60_000;
/** How long the screen keeps asking whether the recount has landed. */
const RECOUNT_WAIT_MS = 10 * 60_000;

/**
 * A column: a label and how to read the number off one tier's counts. Null
 * where the tier has no such thing — supplementary cards are never affiliate
 * or employee cards — so the cell shows a dash rather than a misleading zero.
 */
interface Col {
  key: string;
  label: string;
  get: (counts: Counts) => number | null;
  total?: boolean;
}

const columnsOf = (t: Translate): Col[] => [
  { key: "business", label: t("an.business"), get: (c) => c.business },
  { key: "gold", label: t("an.goldMember"), get: (c) => c.gold },
  { key: "affiliates", label: t("an.affiliates"), get: (c) => c.Affiliates },
  { key: "complimentary", label: t("an.complimentary"), get: (c) => c.complimentary },
  { key: "employee", label: t("an.employee"), get: (c) => c.employee },
  { key: "total", label: t("an.total"), get: (c) => c.total, total: true },
];

/** Both tiers of a club, the primary line first: it is the account the other hangs off. */
const TIERS = [
  { key: "primary", label: "an.primary", of: (r: ClubRow) => r.primary },
  { key: "supplementary", label: "an.supplementary", of: (r: ClubRow) => r.Supplementary },
] as const;
type Tier = (typeof TIERS)[number];
type TierKey = Tier["key"];

/** Supplementary has no affiliates or employees; the API sends zeros there. */
const PRIMARY_ONLY = new Set(["affiliates", "employee"]);
const cell = (tier: TierKey, col: Col, counts: Counts) =>
  tier === "supplementary" && PRIMARY_ONLY.has(col.key) ? null : col.get(counts);

const num = (value: number | null) => (value === null ? "—" : value.toLocaleString());

function MemberAnalyticsPage() {
  const t = useTranslation();
  const [search, setSearch] = useState("");
  // Which tier to show: both lines a club, or just one. On one tier the
  // columns that tier does not have go too, so nobody reads a column of dashes.
  const [tierKey, setTierKey] = useState<"all" | TierKey>("all");
  const tiers = useMemo<readonly Tier[]>(
    () => (tierKey === "all" ? TIERS : TIERS.filter((tier) => tier.key === tierKey)),
    [tierKey],
  );
  const columns = useMemo(
    () => columnsOf(t).filter((c) => tierKey !== "supplementary" || !PRIMARY_ONLY.has(c.key)),
    [t, tierKey],
  );
  const [sort, setSort] = useState<{ key: string; dir: "asc" | "desc" }>({
    key: "club_name",
    dir: "asc",
  });
  // With both lines on show, a column can rank clubs by either line or by
  // the two together; the choice sits beside the Account Type switch.
  const [sortTier, setSortTier] = useState<"both" | TierKey>("both");

  // While a recount is in flight the screen polls until the stamp moves, or
  // gives up after a while: the worker may be down, and a poll for ever is a leak.
  const [awaiting, setAwaiting] = useState<{ was: string | null; until: number } | null>(null);
  const analytics = useQuery({
    queryKey: ["/member-analytics"],
    queryFn: async ({ signal }) =>
      (await api.get<Analytics>("/member-analytics", { signal })).data,
    staleTime: 60_000,
    refetchInterval: (query) => {
      if (!awaiting) return false;
      const landed = query.state.data && query.state.data.refreshed_at !== awaiting.was;
      if (landed) toast.success(t("an.recounted"));
      if (landed || Date.now() >= awaiting.until) {
        setAwaiting(null);
        return false;
      }
      return 3_000;
    },
  });
  const recount = useMutation({
    mutationFn: async () =>
      (await api.post<{ queued: boolean }>("/member-analytics/refresh")).data,
    onSuccess: () =>
      setAwaiting({
        was: analytics.data?.refreshed_at ?? null,
        until: Date.now() + RECOUNT_WAIT_MS,
      }),
  });
  const countedAt = analytics.data?.refreshed_at ? new Date(analytics.data.refreshed_at) : null;
  // Age as of the fetch, so render reads no clock; the API refuses a recount under ten anyway.
  const canRecount =
    !!analytics.data?.changed_since_refresh &&
    (countedAt === null || analytics.dataUpdatedAt - countedAt.getTime() >= RECOUNT_AFTER_MS);

  // A club sorts by the tiers on show together: the pair is one row of the eye.
  const rows = useMemo(() => {
    const all = analytics.data?.member_data ?? [];
    const needle = search.trim().toLowerCase();
    const filtered = needle
      ? all.filter((r) => r.club_name.toLowerCase().includes(needle))
      : all;
    const col = columns.find((c) => c.key === sort.key);
    const sign = sort.dir === "asc" ? 1 : -1;
    const sortTiers = tiers.filter((tier) => sortTier === "both" || tier.key === sortTier);
    const value = (r: ClubRow) =>
      col
        ? sortTiers.reduce((acc, tier) => acc + (cell(tier.key, col, tier.of(r)) ?? 0), 0)
        : 0;
    return [...filtered].sort((a, b) =>
      col ? (value(a) - value(b)) * sign : a.club_name.localeCompare(b.club_name) * sign,
    );
  }, [analytics.data, search, sort, sortTier, columns, tiers]);

  const sum = (tier: Tier, col: Col) =>
    rows.reduce((acc, row) => acc + (cell(tier.key, col, tier.of(row)) ?? 0), 0);
  const totals = {
    primary: rows.reduce((acc, row) => acc + row.primary.total, 0),
    supplementary: rows.reduce((acc, row) => acc + row.Supplementary.total, 0),
  };

  const sortBy = (key: string) =>
    setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));

  const exportCsv = () => {
    const head = [t("an.clubName"), t("an.accountType"), ...columns.map((c) => c.label)];
    const body = rows.flatMap((r) =>
      tiers.map((tier) => [
        r.club_name,
        t(tier.label),
        ...columns.map((c) => cell(tier.key, c, tier.of(r)) ?? ""),
      ]),
    );
    const csv = [head, ...body]
      .map((line) => line.map((v) => `"${String(v)}"`).join(","))
      .join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    Object.assign(document.createElement("a"), {
      href: url,
      download: "member-analytics.csv",
    }).click();
    URL.revokeObjectURL(url);
  };

  const head = (key: string, label: string, align: "left" | "right") => (
    <th
      key={key}
      scope="col"
      aria-sort={
        sort.key === key ? (sort.dir === "asc" ? "ascending" : "descending") : undefined
      }
      className={cn(
        "hairline-b px-3 py-2.5",
        align === "right" && "text-right",
        key === "club_name" && "bg-surface-sunken sticky left-0 z-10",
      )}
    >
      <button
        type="button"
        onClick={() => sortBy(key)}
        className="group/sort text-fg-muted text-subhead hover:text-fg inline-flex items-center gap-1 font-medium transition-colors"
      >
        {label}
        {sort.key === key ? (
          sort.dir === "asc" ? (
            <ArrowUp className="text-accent size-3" aria-hidden />
          ) : (
            <ArrowDown className="text-accent size-3" aria-hidden />
          )
        ) : (
          <ChevronsUpDown
            className="text-fg-subtle size-3 opacity-0 transition-opacity group-hover/sort:opacity-100"
            aria-hidden
          />
        )}
      </button>
    </th>
  );

  return (
    <Page>
      <PageHeader
        title={t("nav.memberAnalytics")}
        description={t("an.description")}
        actions={
          <>
            <RefreshButton queryKeys={[["/member-analytics"]]} />
            <Button variant="secondary" onClick={exportCsv}>
              <Download aria-hidden /> {t("rp.export")}
            </Button>
          </>
        }
      />

      {/* The figures are a count the worker made, not the table as of now: say
          when, and offer a recount once it is old and a member has changed. */}
      {analytics.data ? (
        <p className="text-fg-muted text-subhead mb-4 flex flex-wrap items-center gap-x-3 gap-y-1">
          <Info className="size-4 shrink-0" aria-hidden />
          <span>
            {countedAt
              ? t("an.countedAt").replace("{when}", countedAt.toLocaleString())
              : t("an.notCounted")}
            {countedAt && !analytics.data.changed_since_refresh ? ` ${t("an.upToDate")}` : null}
          </span>
          {awaiting ? (
            <span className="inline-flex items-center gap-1">
              <RotateCw className="size-3.5 animate-spin" aria-hidden /> {t("an.recounting")}
            </span>
          ) : canRecount ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => recount.mutate()}
              disabled={recount.isPending}
            >
              <RotateCw aria-hidden /> {t("an.recount")}
            </Button>
          ) : null}
        </p>
      ) : null}

      {/* The headline first: four numbers answer "how big is this" without
          reading a single table row, and they fit any width. */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat
          label={t("an.totalMembers")}
          value={totals.primary + totals.supplementary}
          emphasis
        />
        <Stat label={t("an.primary")} value={totals.primary} />
        <Stat label={t("an.supplementary")} value={totals.supplementary} />
        <Stat label={t("an.clubs")} value={rows.length} />
      </div>

      <Toolbar className="mt-5 flex flex-wrap items-center gap-3">
        <SearchInput
          containerClassName="w-full sm:w-64"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onClear={() => setSearch("")}
          placeholder={t("an.searchClub")}
          aria-label={t("an.searchClub")}
        />
        <Segmented
          label={t("an.accountType")}
          value={tierKey}
          onChange={setTierKey}
          items={[
            { value: "all", label: t("mem.all") },
            ...TIERS.map((tier) => ({ value: tier.key, label: t(tier.label) })),
          ]}
        />
        {tiers.length > 1 ? (
          <label className="text-fg-muted text-subhead flex items-center gap-2">
            {t("an.sortBy")}
            <Segmented
              label={t("an.sortBy")}
              value={sortTier}
              onChange={setSortTier}
              items={[
                { value: "both", label: t("an.both") },
                ...TIERS.map((tier) => ({ value: tier.key, label: t(tier.label) })),
              ]}
            />
          </label>
        ) : null}
      </Toolbar>

      {analytics.isPending ? (
        <Loading busy action="loading" subject={t("nav.memberAnalytics")} className="mt-6">
          <SkeletonLines rows={6} />
        </Loading>
      ) : (
        // One table, two lines a club: primary above supplementary, the club
        // name spanning both. The two tiers read against each other on the
        // same columns, which two separate panels never let them do, and in
        // the same ink: the second line is not a footnote to the first. The
        // club column is pinned, so a narrow screen scrolling sideways still
        // says whose numbers these are.
        <section className="bg-surface-raised rounded-card border-line mt-4 overflow-hidden border">
          <div className="overflow-x-auto">
            <table className="text-callout w-full">
              <thead className="bg-surface-sunken text-left">
                <tr>
                  {head("club_name", t("an.clubName"), "left")}
                  {tiers.length > 1 ? (
                    <th
                      scope="col"
                      className="hairline-b text-fg-muted text-subhead px-3 py-2.5 font-medium"
                    >
                      {t("an.accountType")}
                    </th>
                  ) : null}
                  {columns.map((c) => head(c.key, c.label, "right"))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  // The club's two lines are one group: a hairline between clubs,
                  // none between a club's own tiers.
                  <Fragment key={r.club_id}>
                    {tiers.map((tier, index) => (
                      <tr
                        key={tier.key}
                        className={cn(
                          "hover:bg-surface-sunken transition-colors",
                          index === 0 && "hairline-t",
                        )}
                      >
                        {index === 0 ? (
                          <td
                            rowSpan={tiers.length}
                            className="bg-surface-raised text-fg sticky left-0 z-10 px-3 py-2.5 align-top font-medium whitespace-nowrap uppercase"
                          >
                            {r.club_name}
                          </td>
                        ) : null}
                        {tiers.length > 1 ? (
                          <td className="text-fg text-subhead px-3 py-2 font-medium whitespace-nowrap">
                            {t(tier.label)}
                          </td>
                        ) : null}
                        {columns.map((c) => (
                          <td
                            key={c.key}
                            className={cn(
                              "text-fg px-3 py-2 text-right tabular-nums",
                              c.total && "bg-accent-soft/40 font-bold",
                            )}
                          >
                            {num(cell(tier.key, c, tier.of(r)))}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </Fragment>
                ))}
                {rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={columns.length + (tiers.length > 1 ? 2 : 1)}
                      className="text-fg-muted px-3 py-10 text-center"
                    >
                      {t("an.noClubs")}
                    </td>
                  </tr>
                ) : null}
              </tbody>
              {rows.length ? (
                // The column sums, one line a tier, so the table foots the way
                // it reads: primary above supplementary here too.
                <tfoot className="hairline-t bg-surface-sunken">
                  {tiers.map((tier, index) => (
                    <tr key={tier.key} className="text-heading font-semibold">
                      {index === 0 ? (
                        <td
                          rowSpan={tiers.length}
                          className="bg-surface-sunken sticky left-0 z-10 px-3 py-2.5 align-top"
                        >
                          {t("an.allClubs")}
                        </td>
                      ) : null}
                      {tiers.length > 1 ? (
                        <td className="text-subhead px-3 py-2 whitespace-nowrap">
                          {t(tier.label)}
                        </td>
                      ) : null}
                      {columns.map((c) => (
                        <td key={c.key} className="px-3 py-2 text-right tabular-nums">
                          {num(
                            tier.key === "supplementary" && PRIMARY_ONLY.has(c.key)
                              ? null
                              : sum(tier, c),
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tfoot>
              ) : null}
            </table>
          </div>
        </section>
      )}
    </Page>
  );
}

/** One headline number. The label is above it, small; the number is the point. */
function Stat({
  label,
  value,
  emphasis,
}: {
  label: string;
  value: number;
  emphasis?: boolean;
}) {
  return (
    <div className="bg-surface-raised rounded-card border-line border px-4 py-3">
      <p className="text-fg-muted text-subhead">{label}</p>
      <p
        className={cn(
          "text-heading mt-1 tabular-nums",
          emphasis ? "text-title-1" : "text-title-2",
        )}
      >
        {num(value)}
      </p>
    </div>
  );
}

export default MemberAnalyticsPage;
